import React, { useState } from 'react';
import { Title } from '../page-components/Title';
import { getFormattedDate } from '../../../utils/utilities';
import { LinkComponent } from '../../LinkComponent';
import './ClientsLogsTableRowAdditionalData.scss';
import clsx from 'clsx';
import { IModalWindow, ModalWindow } from '../../modal/ModalWindow';
import { IClientLogsRowData } from '../../../views/developers/ClientLogs';
import { getReasonPhrase } from 'http-status-codes';
import { errorAlert } from '../../ToastMessage';

const isJsonString = (string): boolean => {
	try {
		JSON.parse(string);
	} catch (e) {
		return false;
	}
	return true;
};

interface Props {
	isOpen: boolean;
	data: IClientLogsRowData;
}

export const ClientsLogsTableRowAdditionalData = (props: Props): React.ReactElement => {
	const { statusCode, description, date, environment, request, response } = props.data;
	const { orderId, payload: requestPayload } = request;
	const { latency, payload: responsePayload } = response;
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const onCloseModal = (): void => {
		setModalState(prevState => ({
			...prevState,
			isOpen: false
		}));
	};

	const handleModalOpen = (payload): void => {
		if (!isJsonString(payload)) {
			errorAlert({
				title: 'Failed'
			});
			return;
		}
		const parsedJSON = JSON.parse(payload);
		const json = JSON.stringify(parsedJSON, null, 4);
		setModalState({
			isOpen: true,
			modalTitle: 'Log information',
			contentClassName: 'd-block pb-1 log-scrollbar',
			isRightSideModal: true,
			content: (
				<div className="sense-script m-6">
					<pre className="code-area p-5">
						{json}
					</pre>
				</div>
			)
		});
	};

	const detailsRequestFields = [
		{
			label: 'Id',
			value: request?.id
		},
		{
			label: 'Environment',
			value: environment
		},
		{
			label: 'Timestamp',
			value: date
		},
		{
			label: 'Order Id',
			value: orderId
		}
	];

	const detailsResponseFields = [
		{
			label: 'Latency',
			value: latency ? `${latency} ms` : '-'
		},
		{
			label: 'Response code',
			value: statusCode ? `${statusCode}, ${getReasonPhrase(statusCode)}` : '-'
		},
		{
			label: 'Response message',
			value: description
		}

	];

	const getValueField = (label, value): JSX.Element => {
		switch (label) {
			case 'Timestamp':
				return (
					<>
						<div className="fs-7 fs-sm-6 me-2">
							{getFormattedDate(date, 'dd MMM, yyyy')},
						</div>
						<div className="fs-7 fs-sm-6">
							{getFormattedDate(date, 'hh:mm a')}
						</div>
					</>
				);
			case 'Error':
				return (
					<div>
						<div className="fs-6">{value}</div>
						<div className="mt-3 fs-8">
							<span className="text-muted">To learn more</span>{' '}
							<LinkComponent text="view the docs" link="#" isUnderLine/>
						</div>
					</div>
				);
			default:
				return <div className="fs-7 fs-sm-6">{value}</div>;
		}
	};
	return (
		<>
			<tr
				className={clsx(
					'transition-height position-relative additional-row-borders',
					props?.isOpen ? 'row-normal-height' : 'row-zero-height'
				)}
			>
				<td className="position-absolute w-100 p-10 ">
					<Title title="Request" additionalClass="fw-bolder fs-5"/>
					{
						detailsRequestFields.map(({ label, value }): JSX.Element => (
							<div key={label} className="d-sm-flex align-items-center mt-5 ms-sm-5">
								<div className="fs-6 w-150px fw-bolder">{label}:</div>
								{getValueField(label, value)}
							</div>
						))
					}
					{
						requestPayload && (
							<div
								className="ms-sm-5 mt-5 text-muted text-decoration-underline fs-sm-6 cursor-pointer"
								onClick={(): void => handleModalOpen(requestPayload)}
							>
								Show more information
							</div>
						)
					}

					<div className="border-bottom my-10"/>

					<Title title="Response" additionalClass="fw-bolder fs-5"/>
					{
						detailsResponseFields.map(({ label, value }): JSX.Element => (
							<div key={label} className="d-sm-flex mt-5 ms-sm-5 align-items-start">
								<div className="fs-6 w-150px fw-bolder">{label}:</div>
								{getValueField(label, value)}
							</div>
						))
					}
					{
						responsePayload && (
							<div
								className="ms-sm-5 mt-5 text-muted text-decoration-underline fs-sm-6 cursor-pointer"
								onClick={(): void => handleModalOpen(responsePayload)}
							>
								Show more information
							</div>
						)
					}
				</td>
			</tr>
			<ModalWindow
				{...modalState}
				onClose={onCloseModal}
			/>
		</>
	);
};
